import { Search, X } from 'lucide-react';

import { cn } from '@/lib/utils';

import { commitIcon, toneDotClasses } from '../lib/commit-format';
import type { CommitTipo, CommitTipoResumen } from '../updates.types';

interface UpdatesFiltersProps {
  query: string;
  onQueryChange: (value: string) => void;
  tipos: readonly CommitTipoResumen[];
  tipoActivo: CommitTipo | null;
  onTipoChange: (tipo: CommitTipo | null) => void;
  total: number;
}

/** Buscador y filtro por tipo de cambio de la bitácora. */
export function UpdatesFilters({
  query,
  onQueryChange,
  tipos,
  tipoActivo,
  onTipoChange,
  total,
}: UpdatesFiltersProps) {
  return (
    <div className="space-y-3">
      <div className="relative">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-3.5 top-1/2 size-4.5 -translate-y-1/2 text-ink-400"
        />
        <input
          type="search"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder="Buscar un cambio… (ej. impresión, kardex, liquidación)"
          aria-label="Buscar en la bitácora de cambios"
          className={cn(
            'h-12 w-full rounded-control border border-ink-200 bg-white pl-11 pr-11 text-sm text-ink-900',
            'placeholder:text-ink-400 transition-all focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none',
          )}
        />

        {query !== '' ? (
          <button
            type="button"
            onClick={() => onQueryChange('')}
            aria-label="Limpiar búsqueda"
            className="absolute right-3 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-600"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {tipos.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            aria-pressed={tipoActivo === null}
            onClick={() => onTipoChange(null)}
            className={cn(
              'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
              tipoActivo === null
                ? 'border-brand-300 bg-brand-50 text-brand-700'
                : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-600',
            )}
          >
            Todos los cambios
            <span className="rounded-full bg-white/70 px-1.5 text-[11px] font-semibold text-ink-500">
              {total}
            </span>
          </button>

          {tipos.map((info) => {
            const activo = info.tipo === tipoActivo;
            const Icon = commitIcon(info.tipo);

            return (
              <button
                key={info.tipo}
                type="button"
                aria-pressed={activo}
                onClick={() => onTipoChange(activo ? null : info.tipo)}
                className={cn(
                  'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                  activo
                    ? 'border-brand-300 bg-white font-semibold text-brand-700'
                    : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-600',
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex size-4 items-center justify-center rounded-full',
                    toneDotClasses(info.tone),
                  )}
                >
                  <Icon className="size-2.5 text-white" />
                </span>
                {info.label}
                <span className="rounded-full bg-white/70 px-1.5 text-[11px] font-semibold text-ink-500">
                  {info.total}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
